import { Calendar, Clock, MapPin, Ticket, Zap } from "lucide-react";
import { Card, CardContent } from "@/shadcn/ui/card";

import { Comedian } from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { Follow } from "@/components/checkout";
import { PunchupButton } from "@/components/shared/PunchupButton";
import { Separator } from "@/shadcn/ui/separator";
import ShareButton from "@/components/common/ShareButton";
import { ShowWithComedian } from "@/data/shows";
import EventWaitlistCta from "./EventWaitlistCta";
import { cn } from "@/utils/cn";
import dayjs from "@punchup/utils/dayjs";

interface EventTicketCardProps {
  show: ShowWithComedian;
  comedian: Comedian;
  eventDate: dayjs.Dayjs;
  isUpcoming: boolean;
  isSoldOut: boolean;
  isTixologiShow: boolean;
  isPunchupComedian: boolean;
  isMultiComic: boolean;
  isFollowing: boolean;
  authenticated: boolean;
  onTicketClick: () => void;
  onVIPTicketClick: () => void;
}

export default function EventTicketCard({
  show,
  comedian,
  eventDate,
  isUpcoming,
  isSoldOut,
  isTixologiShow,
  isPunchupComedian,
  isMultiComic,
  isFollowing,
  authenticated,
  onTicketClick,
  onVIPTicketClick,
}: EventTicketCardProps) {
  const isWaitlistBranch = isUpcoming && isSoldOut;

  return (
    <div className="space-y-6 order-1 lg:order-2">
      {/* Ticket Purchase Card */}
      {/* Offset clears the fixed site header (sticky activated when the page
          shell moved to overflow-x-clip — see ScrollablePageLayout). */}
      <Card className="lg:sticky lg:top-[calc(var(--header-height)+1rem)]">
        <CardContent className={cn(isWaitlistBranch ? "p-4 lg:p-6" : "p-6")}>
          <h3
            className={cn(
              "font-semibold font-sans",
              isWaitlistBranch
                ? "text-lg mb-3 lg:text-xl lg:mb-4"
                : "text-xl mb-4"
            )}
          >
            {isWaitlistBranch ? "Join the Waitlist" : "Buy Tickets"}
          </h3>

          {/* Event Quick Info — hidden on the waitlist branch because the
              hero already shows the same date / time / venue. */}
          {!isWaitlistBranch && (
            <>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span>{eventDate.format("dddd, MMMM D, YYYY")}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span>{eventDate.format("h:mm A")}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>{show.venue}</span>
                </div>
              </div>

              <Separator className="mb-6" />
            </>
          )}

          {/* Ticket Buttons */}
          <div className="space-y-3">
            {isUpcoming ? (
              <>
                {isSoldOut ? (
                  <EventWaitlistCta show={show} comedian={comedian} />
                ) : (
                  <PunchupButton
                    onClick={onTicketClick}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      onTicketClick();
                    }}
                    disabled={!show.ticket_link && !isTixologiShow}
                    className="w-full relative z-10"
                    size="lg"
                  >
                    {isTixologiShow ? (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Fast Checkout
                      </>
                    ) : (
                      <>
                        <Ticket className="w-4 h-4 mr-2" />
                        Buy Tickets
                      </>
                    )}
                  </PunchupButton>
                )}

                {show.vip_ticket_link && (
                  <PunchupButton
                    onClick={onVIPTicketClick}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      onVIPTicketClick();
                    }}
                    variant="outline"
                    className="w-full relative z-10"
                    size="lg"
                  >
                    VIP Tickets
                  </PunchupButton>
                )}

                {show.presale_code && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm font-medium">Presale Code:</p>
                    <p className="text-lg font-bold">{show.presale_code}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4 text-gray-500">
                This event has already taken place
              </div>
            )}
          </div>

          <Separator className="my-6" />

          {/* Follow/Subscribe for Punchup Comedians. Hidden on the waitlist
              branch so the waitlist is the only primary CTA on that surface. */}
          {isPunchupComedian && !isMultiComic && !isWaitlistBranch && (
            <>
              <Follow
                isLoggedOutState={!authenticated}
                redirectTo={false}
                buttonText="Subscribe"
                followedLocation={"event page" as any}
                comedianToFollow={comedian}
                isSubscribed={isFollowing}
                customCSSButtonClass="btn items-center shadow-sm w-full"
                customCSSContainerClass="w-full"
              />
              <Separator className="my-6" />
            </>
          )}

          {/* Share Action */}
          <div className="space-y-3">
            <ShareButton
              shareData={{
                title: show.title || `${comedian.display_name} Live`,
                text: `Check out ${comedian.display_name} live at ${show.venue}!`,
                url: typeof window !== "undefined" ? window.location.href : "",
              }}
              location="event page"
              className="w-full"
              classicButton={true}
              label="Share Event"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
